import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import { fetchPublicationByIssueYearGroups, fetchPublicationByIssueList } from "../../../../services/publicationSearchService";
import { Link, useNavigate } from "react-router-dom";
import { buildPublicationPath } from "../../../../utils/slugify";
import { getArticlesByPublicationIdAndType } from "../../../../services/publisherService";
import { pdfViewerPublicationIdAndPublicationTypeByBookMarkListStatusNotMinusOne } from "../../../../services/publicationService";
import { AppContext } from "../../../../services/AuthContext";
import PublicationCard from "../courseList/components/PublicationCard";
import Spinner from "react-bootstrap/Spinner";
import PropTypes from "prop-types";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from "../../../../services/axiosInstance";
import SaveModal from "../courseList/components/SaveModal";


const pageSize = 20;

/* -------------------- utils -------------------- */
const getAttr = (issue, name) =>
  issue?.publicationtypeInfo?.find(a => a?.attributename === name)?.value?.[0]?.value?.[0] ??
  issue?.templateInfo?.find(a => a?.attributename === name)?.value?.[0]?.value?.[0] ?? null;

const getAttrValue = (arr = [], name) => {
  const row = arr?.find(a => a?.attributename === name);
  const v = row?.value?.[0]?.value?.[0];
  return (v === null || v === undefined) ? "" : String(v).trim();
};

const parseStructuredDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s.replace(" ", "T"));
    return isNaN(d) ? null : d;
  }

  const dmY = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dmY) {
    const [, dd, mm, yyyy] = dmY.map(Number);
    const d = new Date(yyyy, mm - 1, dd);
    return isNaN(d) ? null : d;
  }

  return null;
};

const getIssueDate = (issue) => {
  if (!issue) return null;

  let d = parseStructuredDate(getAttrValue(issue.publicationtypeInfo, "Yayın Tarihi"));
  if (d) return d;

  d = parseStructuredDate(getAttrValue(issue.templateInfo, "Yayın Tarihi"));
  if (d) return d;

  d = parseStructuredDate(getAttrValue(issue.templateInfo, "Erişim Tarihi"));
  if (d) return d;

  const year = Number(getAttr(issue, "Cilt")) || null;
  const no = Number(getAttr(issue, "Sayı")) || null;
  if (year) {
    const week = Math.max(1, no || 1);
    const approx = new Date(year, 0, 1 + (week - 1) * 7);
    return isNaN(approx) ? null : approx;
  }

  return null;
};

const sortIssuesByDateAsc = (issues = []) =>
  [...issues].sort((a, b) => {
    const ta = getIssueDate(a)?.getTime() ?? -Infinity;
    const tb = getIssueDate(b)?.getTime() ?? -Infinity;
    if (ta !== tb) return ta - tb;

    const ya = Number(getAttr(a, "Cilt")) || -Infinity;
    const yb = Number(getAttr(b, "Cilt")) || -Infinity;
    if (ya !== yb) return ya - yb;

    const na = Number(getAttr(a, "Sayı")) || -Infinity;
    const nb = Number(getAttr(b, "Sayı")) || -Infinity;
    if (na !== nb) return na - nb;

    return (a.mysqlId ?? -Infinity) - (b.mysqlId ?? -Infinity);
  });

const extractYear = (issue) => {
  if (!issue) return null;
  const dateStr =
    getAttrValue(issue.publicationtypeInfo, "Yayın Tarihi") ||
    getAttrValue(issue.templateInfo, "Yayın Tarihi") ||
    getAttr(issue, "Yıl");
  const m = String(dateStr || "").match(/\d{4}/);
  if (m) return parseInt(m[0], 10);
  const cilt = Number(getAttr(issue, "Cilt")) || null;
  return cilt;
};
/* ------------------------------------------------ */

const LatestIssue = ({ publicationId, onLatestYearFound, publicationType, onCite, onAddToLibrary, titleOverride, initialIssue, favoriteSavedIds, userinstDatabases, articleTypeOverride, onCoverImageUpdate }) => {
  const { auth } = useContext(AppContext);
  const navigate = useNavigate();

  const [years, setYears] = useState([]);               // [2024, 2023, ...] (desc)
  const [yearIssueMap, setYearIssueMap] = useState({}); // {2024: [ASC issues], ...}
  const [currentYear, setCurrentYear] = useState(null);
  const [currentIssueIndex, setCurrentIssueIndex] = useState(0);

  const [articles, setArticles] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [articlesLoading, setArticlesLoading] = useState(false);
  // summary open state for PublicationCard show/hide (per-item)
  const [openSummaryId, setOpenSummaryId] = useState(null);
  const [bookmarkList, setBookmarkList] = useState([]);



  const headers = auth.token
    ? {
      Authorization: `Bearer ${auth.token}`,
    }
    : "";

  const handleKeywordClick = (keyword) => {
    window.open(`/publication-list?databases=&documentTypes=&endyear=&journals=&keywords=${encodeURIComponent(keyword)}&languages=&permission=-1&publicationAdvantageSearchDTOList=&publishers=&startyear=&status=1&topicTitles=`, "_blank");
  };

  // 1) Yılları çek
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (publicationType) {
          const yearsRes = await fetchPublicationByIssueYearGroups(publicationId, publicationType, 1);


          if (yearsRes?.status === "OK" && Array.isArray(yearsRes.data) && yearsRes.data.length > 0) {
            const list = yearsRes.data
              .map(y => parseInt(y.key, 10))
              .filter(n => !isNaN(n))
              .sort((a, b) => b - a); // yıllar desc (2024, 2023, ...)
            if (!alive) return;
            setYears(list);

            if (onLatestYearFound) {
              onLatestYearFound(list[0]);
            }

            const defaultYear = list[0]; // En son yıl
            const initYear = initialIssue ? extractYear(initialIssue) : defaultYear;
            setCurrentYear(list.includes(initYear) ? initYear : defaultYear);
          } else {
            if (!alive) return;
            setYears([]);
            setCurrentYear(null);
          }
        }
      } catch {
        if (!alive) return;
        setYears([]);
        setCurrentYear(null);
      }
    })();
    return () => { alive = false; };
  }, [publicationId, publicationType, initialIssue]);

  // 2) Seçili yılın issues’unu cache’le **ASC** olarak
  useEffect(() => {
    if (!currentYear || yearIssueMap[currentYear]) return;
    let alive = true;
    (async () => {
      try {
        const issuesRes = await fetchPublicationByIssueList(publicationId, publicationType, currentYear, 1);
        const raw = (issuesRes?.status === "OK" && Array.isArray(issuesRes.data)) ? issuesRes.data : [];
        const asc = sortIssuesByDateAsc(raw); // Ocak→Aralık
        if (!alive) return;
        setYearIssueMap(prev => ({ ...prev, [currentYear]: asc }));
      } catch {
        if (!alive) return;
        setYearIssueMap(prev => ({ ...prev, [currentYear]: [] }));
      }
    })();
    return () => { alive = false; };
  }, [currentYear, publicationId, publicationType, yearIssueMap]);

  // 3) Komşu yılları prefetch (ASC)
  useEffect(() => {
    if (!currentYear || !years.length) return;
    const yi = years.indexOf(currentYear);
    const neighbors = [years[yi - 1], years[yi + 1]].filter(Boolean);
    neighbors.forEach(async (y) => {
      if (y && !yearIssueMap[y]) {
        try {
          const res = await fetchPublicationByIssueList(publicationId, publicationType, y, 1);
          const raw = (res?.status === "OK" && Array.isArray(res.data)) ? res.data : [];
          const asc = sortIssuesByDateAsc(raw);
          setYearIssueMap(prev => ({ ...prev, [y]: asc }));
        } catch {
          setYearIssueMap(prev => ({ ...prev, [y]: [] }));
        }
      }
    });
  }, [currentYear, years, yearIssueMap, publicationId, publicationType]);

  // 4) Bu yılın ASC listesi & currentIssue
  const issues = yearIssueMap[currentYear] || [];

  // out-of-bounds koruması ve **EN SON SAYI SEÇİMİ**
  useEffect(() => {
    if (!issues.length) return;

    // 1. InitialIssue varsa, o sayının indeksini bul ve ayarla.
    if (initialIssue) {
      const idx = issues.findIndex(i => String(i.mysqlId) === String(initialIssue.mysqlId));
      if (idx >= 0) {
        setCurrentIssueIndex(idx);
        return; // initialIssue varsa, varsayılanı (son sayı) ezme.
      }
    }

    // 2. InitialIssue yoksa veya bulunamadıysa:
    // Eğer indeks 0 ise VEYA out-of-bounds ise, en son sayıya (issues.length - 1) ayarla.
    // İlk yüklendiğinde currentIssueIndex 0 olduğu için, bu onu yılın son sayısı yapar.
    if (currentIssueIndex === 0 || currentIssueIndex >= issues.length) {
      setCurrentIssueIndex(issues.length - 1);
    }
  }, [issues, currentIssueIndex, initialIssue]);

  const currentIssue = useMemo(() => {
    if (!issues.length) return null;
    return issues[currentIssueIndex] || null;
  }, [issues, currentIssueIndex]);

  const effectiveType = useMemo(() => {
    if (!currentIssue) return "Popüler Dergi Yazısı"; // Fallback (yüklenene kadar)

    const rawType = currentIssue.publicationtype || "";

    // API'den gelen "Popüler Dergi Sayısı" ise -> "Popüler Dergi Yazısı"
    if (rawType.includes("Popüler")) {
      return "Popüler Dergi Yazısı";
    }

    // API'den gelen "Akademik Dergi Sayısı" veya "Akademik Kitap" ise -> "Akademik Makale"
    if (rawType.includes("Akademik")) {
      return "Akademik Makale";
    }
    // Eğer hiçbir şey bulunamazsa güvenli bir varsayılan dön
    return "Akademik Makale";
  }, [currentIssue]); // currentIssue değiştikçe (sayı değiştikçe) bu da güncellenir

  // 1. Sayı değiştiğinde hem makaleleri hem bookmarkları sıfırlayalım
  useEffect(() => {
    setPage(1);
    setArticles([]);
    setBookmarkList([]); // Bunu ekledik
    setHasMore(true);
  }, [currentIssue]);

  // 5) Prev/Next:
  //  - Geri (←): aynı yılda idx-1; yıl sınırında **önceki yılın Ocak'ı** (ilk)
  //  - İleri (→): aynı yılda idx+1; yıl sınırında **sonraki yılın Aralık'ı** (son)
  const getPrevIssue = () => {
    if (!currentIssue || !currentYear) return null;
    const list = yearIssueMap[currentYear] || [];
    const idx = list.findIndex(x => String(x.mysqlId) === String(currentIssue.mysqlId));

    if (idx > 0) return list[idx - 1]; // aynı yılda geri

    // yıl sınırı (Ocak'tan geri) → önceki yılın Ocak'ı
    const yi = years.indexOf(currentYear);
    const prevYear = years[yi + 1]; // years desc → +1 = önceki yıl
    if (!prevYear) return null;
    const prevList = yearIssueMap[prevYear] || [];
    return prevList.length ? prevList[prevList.length - 1] : null; // Aralık

  };

  const getNextIssue = () => {
    if (!currentIssue || !currentYear) return null;
    const list = yearIssueMap[currentYear] || [];
    const idx = list.findIndex(x => String(x.mysqlId) === String(currentIssue.mysqlId));

    if (idx >= 0 && idx < list.length - 1) return list[idx + 1]; // aynı yılda ileri

    // yıl sınırı (Aralık'tan ileri) → sonraki yılın Aralık'ı
    const yi = years.indexOf(currentYear);
    const nextYear = years[yi - 1]; // years desc → -1 = sonraki yıl
    if (!nextYear) return null;
    const nextList = yearIssueMap[nextYear] || [];
    return nextList.length ? nextList[0] : null; // Ocak

  };

  const prevIssue = getPrevIssue();
  const nextIssue = getNextIssue();

  // 1. Fonksiyonları useCallback ile sabitleyelim
  const fetchArticlesAction = useCallback(async (issueId, currentPage) => {
    try {
      const res = await getArticlesByPublicationIdAndType(
        issueId,
        effectiveType,
        auth.token,
        currentPage,
        pageSize
      );
      return (res && Array.isArray(res.data)) ? res.data : [];
    } catch {
      return [];
    }
  }, [auth.token, effectiveType]);

  const fetchBookmarksAction = useCallback(async (issueId) => {
    try {
      const res = await pdfViewerPublicationIdAndPublicationTypeByBookMarkListStatusNotMinusOne(
        issueId,
        effectiveType,
        auth.token
      );
      return (res?.status === "OK" && Array.isArray(res.data)) ? res.data : [];
    } catch {
      return [];
    }
  }, [auth.token, effectiveType]);

  // 2. Ana useEffect: Süreci yöneten kısım
  useEffect(() => {
    let isMounted = true;

    const loadContent = async () => {
      if (!currentIssue) return;

      // Süreç başlarken loading true
      setArticlesLoading(true);

      // İlk sayfadaysak ve Akademik Makale DEĞİLSE, önce BOOKMARK (alt yayın/içindekiler) deneyelim
      if (page === 1 && effectiveType !== "Akademik Makale") {
        const bookmarkRes = await fetchBookmarksAction(currentIssue.mysqlId);
        if (!isMounted) return;

        if (bookmarkRes && bookmarkRes.length > 0) {
          // Bookmark bulundu ise
          setBookmarkList(bookmarkRes);
          setArticles([]);
          setHasMore(false);
          setArticlesLoading(false);
          return;
        }
      }

      // Bookmark bulunamadıysa VEYA 2. sayfaya geçildiyse (makaleler pagination destekliyorsa)
      // Orijinal makale servisine (getArticlesByPublicationIdAndType) istek atalım
      const articleRes = await fetchArticlesAction(currentIssue.mysqlId, page);

      if (!isMounted) return;

      if (articleRes.length > 0) {
        // Makale varsa listeyi güncelle
        if (page === 1) setArticles(articleRes);
        else setArticles(prev => [...prev, ...articleRes]);

        setHasMore(articleRes.length === pageSize);
        if (page === 1) setBookmarkList([]); // Makale varken sadece ilk sayfada sıfırlayalım ki güvenli olsun
        setArticlesLoading(false); // Makale bulundu, yükleme bitti
      } else {
        // Hiç bir şey bulunamadı veya sonraki sayfalarda veri bitti
        if (page === 1) {
          setArticles([]);
          setBookmarkList([]);
        }
        setHasMore(false);
        setArticlesLoading(false);
      }
    };

    loadContent();

    return () => { isMounted = false; };
  }, [currentIssue, page, fetchArticlesAction, fetchBookmarksAction]);

  // currentIssue değiştiğinde kapak resmini ve ID'sini güncelle
  useEffect(() => {
    if (currentIssue && onCoverImageUpdate) {
      onCoverImageUpdate(currentIssue.coverimage, currentIssue.mysqlId);
    }
  }, [currentIssue, onCoverImageUpdate]);

  const headerTitle = titleOverride || "Son Sayı";

  // tüm yılların tüm issues’u → tek liste
  const allIssues = useMemo(() => {
    return years.flatMap(y => yearIssueMap[y] || []);
  }, [years, yearIssueMap]);

  const issueOptions = useMemo(() => {
    return allIssues.map(i => {
      const y = extractYear(i) ?? "-";
      const vol = getAttr(i, "Cilt") ?? "-";
      const no = getAttr(i, "Sayı") ?? "-";
      const ttl = getAttr(i, "Başlık") || `Yıl ${y} • Cilt ${vol} • Sayı ${no}`;
      return { value: String(i.mysqlId), label: `${ttl} (Yıl ${y}, Cilt ${vol}, Sayı ${no})` };
    });
  }, [allIssues]);

  if (!currentIssue) {
    return (
      <div>
        <h5 className="fw-semibold text-darkfume text-sm mt-0 mb-2">{headerTitle}</h5>
        {/* headerTitle yanına küçük bir badge */}
        <span className="badge bg-light text-dark ms-2" title="Liste tip">
          {articleTypeOverride || "Akademik Makale"}
        </span>

        <hr className="my-3 opacity-50" style={{ borderColor: "#b0b3b8" }} />
        <div className="d-flex align-items-center my-3">
          <Spinner animation="border" size="sm" className="text-info me-2" />
          <span>Yükleniyor...</span>
        </div>
      </div>
    );
  }

  const yearText = extractYear(currentIssue) ?? "-";
  const volumeText = getAttr(currentIssue, "Cilt") ?? "-";
  const issueNoText = getAttr(currentIssue, "Sayı") ?? "-";
  const displayList = articles.length > 0 ? articles : bookmarkList;
  const isNoContent = !articlesLoading && articles.length === 0 && bookmarkList.length === 0;



  return (
    <div>
      <div
        className="d-flex justify-content-between align-items-center mb-2"
        style={{ fontSize: "0.95rem" }}
      >
        {/* sol taraf */}
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="text-darkfume">Başlık:</span>
          <Link
            to={buildPublicationPath({ title: getAttr(currentIssue, "Başlık"), id: currentIssue.mysqlId })}
            className="text-blue btn-link"
          >
            {getAttr(currentIssue, "Başlık") || "Başlık Yok"}
          </Link>

          <span className="mx-2 text-fume">•</span>
          <span className="text-darkfume">Yıl: <strong>{yearText}</strong></span>

          <span className="mx-2 text-fume">•</span>
          <span className="text-darkfume">Cilt: <strong>{volumeText}</strong></span>

          <span className="mx-2 text-fume">•</span>
          <span className="text-darkfume">Sayı: <strong>{issueNoText}</strong></span>
        </div>

        {/* sağ taraf (butonlar) */}
        <div className="d-flex align-items-center gap-2">
          <Link
            to={prevIssue ? buildPublicationPath({ title: getAttr(prevIssue, "Başlık"), id: prevIssue.mysqlId }) : "#"}
            className={`btn-primary rounded-3 btn btn-sm ${!prevIssue ? "disabled" : ""}`}
            style={{ minWidth: 120, pointerEvents: prevIssue ? "auto" : "none" }}
          >
            &#8592; Önceki Sayı
          </Link>
          <Link
            to={nextIssue ? buildPublicationPath({ title: getAttr(nextIssue, "Başlık"), id: nextIssue.mysqlId }) : "#"}
            className={`btn-primary rounded-3 btn btn-sm ${!nextIssue ? "disabled" : ""}`}
            style={{ minWidth: 120, pointerEvents: nextIssue ? "auto" : "none" }}
          >
            Sonraki Sayı &#8594;
          </Link>
        </div>
      </div>

      <hr className="my-3 opacity-50" style={{ borderColor: "#b0b3b8" }} />

      <div className="section-content mb-2 mt-2">
        <div className="mb-3 mt-3">
          {/* 1) Yükleniyor durumu */}
          {articlesLoading && page === 1 && (
            <div className="d-flex align-items-center justify-content-center my-3">
              <Spinner animation="border" size="sm" className="text-info me-2" />
              <span>Yükleniyor...</span>
            </div>
          )}

          {/* 2) Hiç içerik yoksa */}
          {isNoContent && (
            <div className="text-center text-muted py-4">Bu sayıya ait içerik bulunamadı.</div>
          )}

          {/* 3) Liste gösterimi (Makale veya Bookmark) */}
          {!articlesLoading && displayList.length > 0 && (
            <>
              {/* Eğer makale yok da bookmark gösteriyorsak ufak bir bilgi notu ekleyebilirsin (Opsiyonel) */}
              {articles.length === 0 && bookmarkList.length > 0 && (
                <div className="small text-muted text-center mb-2 ps-1">
                  <i className="bx bx-info-circle me-1"></i> Sayı içeriği (Bookmark) listeleniyor.
                </div>
              )}

              {displayList.map((item, idx) => (
                <div key={item.mysqlId || idx} className="mt-2">
                  <PublicationCard
                    pub={item}
                    selected={false}
                    onSelect={() => { }}
                    onDetails={() => window.location.assign(buildPublicationPath({ title: getAttr(item, "Başlık"), id: item.mysqlId }))}
                    onCite={() => (onCite ? onCite(item) : {})}
                    onAddToLibrary={onAddToLibrary}
                    favoriteSavedIds={favoriteSavedIds}
                    userinstDatabases={userinstDatabases}
                    onKeywordClick={handleKeywordClick}
                    isSummaryOpen={openSummaryId === item.mysqlId}
                    onToggleSummary={() =>
                      setOpenSummaryId(prev => (prev === item.mysqlId ? null : item.mysqlId))
                    }
                    style={{ borderRadius: 8, boxShadow: "0 2px 8px #f0f1f2" }}
                  />
                </div>
              ))}

              {/* 4) Sadece gerçek makaleler varken "Daha Fazla" butonu çıksın */}
              {hasMore && articles.length > 0 && (
                <div className="d-flex justify-content-center mt-3">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={articlesLoading}
                  >
                    {articlesLoading ? "Yükleniyor..." : "Daha Fazla Göster"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

LatestIssue.propTypes = {
  publicationId: PropTypes.string.isRequired,
  publicationType: PropTypes.string.isRequired,
  onCite: PropTypes.func,
  onAddToLibrary: PropTypes.func,
  titleOverride: PropTypes.string,
  initialIssue: PropTypes.object,
  articleTypeOverride: PropTypes.string,
};

export default LatestIssue;